import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { ICard } from "../types.ts";
import { cardsAPI } from "../cardsAPI.ts";

const POLL_INTERVAL_MS = 5000; // 5сек
const MAX_TOTAL_TIME_MS = 10 * 60 * 1000; // 10мин

export const cardListQueryKey = "card-list";

export const useGetCardsQuery = () => {
  const [pollingEnabled, setPollingEnabled] = useState(true);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const checkNotLinked = (cards: ICard[]): boolean => {
    return cards.some(card => card.statusCode === "NOT_LINKED");
  };

  const query = useQuery<ICard[]>({
    queryKey: [cardListQueryKey],
    queryFn: ({ signal }) => cardsAPI.getCards(signal),

    staleTime: 5 * 60 * 1000, // 5мин
    gcTime: 15 * 60 * 1000, // 15мин
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: true,

    refetchInterval: (query) => {
      const data = query.state.data;

      if (!pollingEnabled || !Array.isArray(data)) {
        return false;
      }

      const hasNotLinked = checkNotLinked(data);
      return hasNotLinked ? POLL_INTERVAL_MS : false;
    },
    refetchIntervalInBackground: false,
  });

  // Disable polling after 10min
  useEffect(() => {
    if (!query.isSuccess || !Array.isArray(query.data) || !pollingEnabled) {
      return;
    }

    const hasNotLinked = checkNotLinked(query.data);

    if (hasNotLinked && !timeoutRef.current) {
      timeoutRef.current = setTimeout(() => {
        setPollingEnabled(false);
      }, MAX_TOTAL_TIME_MS);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [query.isSuccess, query.data, pollingEnabled]);

  return query;
};
